/**
 * Normalisation des champs de date hétérogènes du repo (#900 / #901 / #905).
 *
 * Un même champ Firestore peut arriver côté client sous quatre formes selon
 * l'ancienneté du document et le chemin d'écriture :
 *   - `Timestamp` Firestore (SDK web / Admin `serverTimestamp()`) → expose `toDate()`
 *   - `{ seconds, nanoseconds }` / `{ _seconds, _nanoseconds }` (Timestamp
 *     sérialisé par une callable Function ou un JSON stocké tel quel)
 *   - `Date` JS (document écrit par le SDK client puis gardé en mémoire)
 *   - string ISO / epoch en millisecondes (imports, legacy)
 *
 * Le piège récurrent est `value instanceof Date ? value : new Date(value)` :
 * appliqué à un `Timestamp`, `new Date(objet)` produit un **Invalid Date**
 * (`NaN`), d'où les `Invalid Date` et `NaN-NaN` affichés en backoffice.
 *
 * Utiliser `toJsDate()` partout où une date Firestore est lue avant affichage,
 * export ou calcul.
 */

/** Toutes les formes de date acceptées en entrée de `toJsDate()`. */
export type FirestoreDateLike =
  | Date
  | string
  | number
  | { toDate?: () => Date; toMillis?: () => number }
  | { seconds?: number; nanoseconds?: number }
  | { _seconds?: number; _nanoseconds?: number }
  | null
  | undefined;

/**
 * Convertit n'importe quelle représentation de date Firestore en `Date` JS.
 *
 * @returns une `Date` valide, ou `null` si la valeur est absente / non
 *          interprétable (jamais un `Invalid Date`).
 *
 * Note : l'epoch 0 (`0` ms, `seconds: 0`) est traité comme « absent » —
 * c'est toujours un champ non renseigné dans nos données, jamais le
 * 1er janvier 1970.
 */
export function toJsDate(value: FirestoreDateLike): Date | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    return value > 0 && isFinite(value) ? new Date(value) : null;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'object') {
    const candidate = value as Record<string, any>;

    // Timestamp Firestore (SDK web ou Admin)
    if (typeof candidate['toDate'] === 'function') {
      const converted = candidate['toDate']();
      return converted instanceof Date && !isNaN(converted.getTime()) ? converted : null;
    }
    if (typeof candidate['toMillis'] === 'function') {
      const millis = candidate['toMillis']();
      return typeof millis === 'number' && millis > 0 ? new Date(millis) : null;
    }

    // Timestamp sérialisé (callable Function, JSON) — les deux conventions
    const seconds = typeof candidate['seconds'] === 'number'
      ? candidate['seconds']
      : (typeof candidate['_seconds'] === 'number' ? candidate['_seconds'] : null);
    if (seconds !== null && seconds > 0) {
      const nanos = typeof candidate['nanoseconds'] === 'number'
        ? candidate['nanoseconds']
        : (typeof candidate['_nanoseconds'] === 'number' ? candidate['_nanoseconds'] : 0);
      return new Date(seconds * 1000 + Math.floor(nanos / 1e6));
    }
  }

  return null;
}

/**
 * Nombre de minutes écoulées depuis minuit (heure locale) pour une date donnée.
 *
 * Sert aux comparaisons horaires : comparer `getMinutes()` seul est faux dès
 * que l'heure diffère (bug #905 — un pointage à 10h00 « n'était pas en retard »
 * face à une limite de 09h15 car `0 > 15` est faux).
 */
export function minutesSinceMidnight(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}
